/**
 * RequestDetailsView — full details drawer for a Service Request.
 *
 * Contains: customer info card, price card, documents grid, chat history,
 * sequential status progression, action buttons, and status history log.
 *
 * Kept as a controlled component: all state is owned by the parent
 * (ServiceRequests page). Parent provides callbacks for advance/cancel.
 */
import { useState } from 'react';
import {
  FileText, User, Phone, Mail, Calendar, Paperclip, ShieldCheck, ShieldAlert,
  MessageCircle, Bot, History, ArrowRight, ArrowLeft, DollarSign, CreditCard,
  Ban, Loader2, Send, Image as ImageIcon, FileIcon, Eye, Download, AlertTriangle, X,
} from 'lucide-react';
import { Button } from '../ui/button';

// ── attachment resolution ───────────────────────────────────────────────────
// The customer platform stores documents_uploaded[].file_url in several shapes:
//   * https URL                       → open directly
//   * data:image/...;base64,...       → preview inline
//   * RAW base64 (no data: prefix)    → detect mime by signature and wrap
//   * truncated stub (e.g. 100 chars ending in "...") → cannot be opened
function resolveFileSrc(fileUrl) {
  if (!fileUrl || ['x', 'y', 'z'].includes(fileUrl)) return { kind: 'none' };
  const s = String(fileUrl).trim();
  if (s.startsWith('http://') || s.startsWith('https://')) {
    return { kind: 'url', src: s, isImage: /\.(png|jpe?g|webp|gif|bmp|heic)(\?|$)/i.test(s) };
  }
  if (s.startsWith('data:')) {
    const mime = (s.match(/^data:([^;,]+)/) || [])[1] || '';
    return { kind: 'data', src: s, isImage: mime.startsWith('image/'), mime };
  }
  const clean = s.replace(/\s/g, '');
  if (/^[A-Za-z0-9+/=.]+$/.test(clean)) {
    // raw base64 — stubs cut by the source end with "..." or are far too short
    if (clean.endsWith('...') || clean.length < 500) return { kind: 'truncated' };
    const mime = clean.startsWith('/9j/') ? 'image/jpeg'
      : clean.startsWith('iVBOR') ? 'image/png'
      : clean.startsWith('R0lGOD') ? 'image/gif'
      : clean.startsWith('UklGR') ? 'image/webp'
      : clean.startsWith('JVBER') ? 'application/pdf'
      : 'application/octet-stream';
    return { kind: 'data', src: `data:${mime};base64,${clean}`, isImage: mime.startsWith('image/'), mime };
  }
  return { kind: 'truncated' };
}

function downloadResolvedDoc(resolved, name) {
  if (resolved.kind === 'url') {
    window.open(resolved.src, '_blank', 'noopener');
    return;
  }
  try {
    const [meta, b64] = resolved.src.split(',');
    const mime = (meta.match(/^data:([^;,]+)/) || [])[1] || 'application/octet-stream';
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    const blobUrl = URL.createObjectURL(new Blob([bytes], { type: mime }));
    const a = document.createElement('a');
    a.href = blobUrl;
    const ext = mime.split('/')[1] || 'bin';
    a.download = `${name || 'document'}.${ext === 'jpeg' ? 'jpg' : ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
  } catch { /* corrupted base64 */ }
}

export function RequestDetailsView({
  request,
  language,
  updatingStatus,
  statuses,
  getNextStatus,
  onAdvance,
  onCancelClick,
}) {
  const [previewDoc, setPreviewDoc] = useState(null);
  if (!request) return null;

  const currentIdx = statuses.findIndex((s) => s.id === request.status);
  const next = request.next_status || getNextStatus(request.status);
  const isTerminal = ['completed', 'cancelled'].includes(request.status);
  const nextInfo = statuses.find((s) => s.id === next);
  const NextIcon = nextInfo?.icon || Send;
  const isPendingPayment = request.status === 'pending_payment';

  return (
    <div className="space-y-6">
      {/* Request Info Card */}
      <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10">
        <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#c9a227]" />
          {language === 'ar' ? 'تفاصيل الطلب' : 'Request Details'} #{request.id?.substring(0, 6)}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoBox icon={User} label={language === 'ar' ? 'اسم العميل' : 'Customer Name'} value={request.customer_name} />
          <InfoBox icon={Phone} label={language === 'ar' ? 'رقم الهاتف' : 'Phone'} value={request.phone} dir="ltr" />
          <InfoBox icon={Mail} label={language === 'ar' ? 'البريد الإلكتروني' : 'Email'} value={request.email} />
          <InfoBox
            icon={Calendar}
            label={language === 'ar' ? 'تاريخ الطلب' : 'Request Date'}
            value={new Date(request.created_at).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-AE')}
          />
        </div>
        {request.details && (
          <div className="mt-4 bg-black/30 rounded-xl p-4 border border-white/5">
            <p className="text-xs text-[#808080] mb-2">{language === 'ar' ? 'تفاصيل الطلب' : 'Request Details'}</p>
            <p className="text-white">{request.details}</p>
          </div>
        )}
      </div>

      {/* Price card */}
      {request.price && (
        <div className="bg-gradient-to-br from-[#d4af37]/15 to-transparent rounded-2xl p-5 border border-[#d4af37]/30" data-testid="sr-price-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] text-[#d4af37]/80 uppercase tracking-wider flex items-center gap-1">
                <DollarSign className="h-3 w-3" />
                {language === 'ar' ? 'السعر المطلوب دفعه' : 'Amount Due'}
              </div>
              <div className="text-2xl font-bold text-[#d4af37] font-mono mt-1">
                {Number(request.price).toLocaleString('en-US', { minimumFractionDigits: 2 })} {request.currency || 'AED'}
              </div>
              {request.payment_notes && (
                <div className="text-xs text-white/70 mt-2">{request.payment_notes}</div>
              )}
            </div>
            <div className="text-end">
              <div className="text-[10px] text-white/50 uppercase">Payment Status</div>
              <div className={`text-sm font-bold mt-1 ${
                request.payment_status === 'paid' ? 'text-emerald-300' : 'text-red-300'
              }`}>
                {request.payment_status === 'paid'
                  ? (language === 'ar' ? '✓ تم الدفع' : '✓ Paid')
                  : (language === 'ar' ? '⏳ في انتظار الدفع' : '⏳ Awaiting Payment')}
              </div>
              {request.payment_reference && (
                <div className="text-[10px] text-white/50 font-mono mt-1">Ref: {request.payment_reference}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Documents */}
      {request.documents_uploaded?.length > 0 && (
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10" data-testid="sr-documents-card">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Paperclip className="h-5 w-5 text-[#c9a227]" />
            {language === 'ar' ? `المستندات المرفقة (${request.documents_uploaded.length})` : `Attached Documents (${request.documents_uploaded.length})`}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {request.documents_uploaded.map((doc, i) => (
              <DocumentCard
                key={doc.doc_id || i}
                doc={doc}
                index={i}
                language={language}
                onPreview={(resolved) => setPreviewDoc({ doc, resolved })}
              />
            ))}
          </div>
        </div>
      )}

      {/* Chat history */}
      {request.chat_history?.length > 0 && (
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10" data-testid="sr-chat-card">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-[#c9a227]" />
            {language === 'ar' ? `محادثة العميل (${request.chat_history.length} رسالة)` : `Customer Conversation (${request.chat_history.length} messages)`}
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto pe-2">
            {request.chat_history.map((msg, i) => (
              <ChatBubble key={i} msg={msg} index={i} language={language} />
            ))}
          </div>
        </div>
      )}

      {/* Status Progression */}
      <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10" data-testid="sr-status-card">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <History className="h-5 w-5 text-[#c9a227]" />
          {language === 'ar' ? 'مسار الحالة' : 'Status Progression'}
        </h3>

        <div className="flex items-center gap-1 mb-6 flex-wrap">
          {statuses.map((status, idx) => {
            const StatusIcon = status.icon;
            const isDone = idx < currentIdx;
            const isCurrent = idx === currentIdx;
            const isCancelled = request.status === 'cancelled';
            return (
              <div key={status.id} className="flex items-center gap-1 flex-1 min-w-[110px]">
                <div
                  className={`flex flex-col items-center gap-1 py-2 px-2 rounded-lg border transition-all flex-1 ${
                    isCurrent && !isCancelled
                      ? 'bg-white/[0.06] border-[#d4af37]/60 shadow-lg'
                      : isDone
                        ? 'bg-emerald-500/5 border-emerald-500/25'
                        : 'bg-black/20 border-white/5 opacity-40'
                  }`}
                  style={isCurrent && !isCancelled ? { boxShadow: `0 0 20px ${status.color}30` } : {}}
                  data-testid={`sr-stage-${status.id}`}
                >
                  <StatusIcon
                    className={`h-4 w-4 ${isCurrent && !isCancelled ? '' : isDone ? 'text-emerald-300' : 'text-white/30'}`}
                    style={isCurrent && !isCancelled ? { color: status.color } : {}}
                  />
                  <span className={`text-[10px] font-semibold text-center leading-tight ${
                    isCurrent && !isCancelled ? 'text-white' : isDone ? 'text-emerald-300' : 'text-white/40'
                  }`}>
                    {language === 'ar' ? status.nameAr : status.nameEn}
                  </span>
                </div>
                {idx < statuses.length - 1 && (
                  <ArrowRight className={`h-3 w-3 shrink-0 ${language === 'ar' ? 'rotate-180' : ''} ${
                    isDone ? 'text-emerald-300' : 'text-white/20'
                  }`} />
                )}
              </div>
            );
          })}
        </div>

        {request.status === 'cancelled' && (
          <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-center gap-2 text-red-300 text-sm">
            <Ban className="h-4 w-4" />
            {language === 'ar' ? 'تم إلغاء هذا الطلب.' : 'This request has been cancelled.'}
          </div>
        )}

        {isTerminal ? (
          <div className="text-center py-4 text-sm text-white/60" data-testid="sr-terminal-msg">
            {request.status === 'completed'
              ? (language === 'ar' ? '✅ الطلب مكتمل بنجاح.' : '✅ Request completed successfully.')
              : ''}
          </div>
        ) : (
          <div className="space-y-3">
            {isPendingPayment && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-start gap-2 text-sm">
                <CreditCard className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />
                <div>
                  <div className="text-amber-300 font-semibold">
                    {language === 'ar' ? 'بانتظار دفع العميل' : 'Waiting for customer payment'}
                  </div>
                  <div className="text-xs text-white/60 mt-0.5">
                    {language === 'ar'
                      ? 'سيتم الانتقال تلقائياً إلى "المعالجة النهائية" بمجرد إتمام العميل الدفع من منصة العملاء.'
                      : 'Will auto-advance to "Final Processing" as soon as the customer completes payment on the customer platform.'}
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Button
                onClick={() => onAdvance(request)}
                disabled={updatingStatus || isPendingPayment}
                className="flex items-center gap-2 bg-gradient-to-r from-[#d4af37] to-[#9b7d20] hover:from-[#e0bd45] hover:to-[#a58a2c] text-black font-bold px-6 py-3 text-base disabled:opacity-40"
                data-testid="sr-advance-btn"
              >
                {updatingStatus ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <NextIcon className="h-5 w-5" />
                    {language === 'ar'
                      ? (next === 'pending_payment' ? 'إرسال طلب الدفع' : `التالي → ${nextInfo?.nameAr}`)
                      : (next === 'pending_payment' ? 'Send Payment Request' : `Next → ${nextInfo?.nameEn}`)}
                    {language === 'ar' ? <ArrowLeft className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
                  </>
                )}
              </Button>
              <Button
                onClick={onCancelClick}
                disabled={updatingStatus}
                variant="ghost"
                className="text-red-300 hover:bg-red-500/10 border border-red-500/25"
                data-testid="sr-cancel-btn"
              >
                <Ban className="h-4 w-4 me-1.5" />
                {language === 'ar' ? 'إلغاء الطلب' : 'Cancel Request'}
              </Button>
            </div>
          </div>
        )}

        {/* Status history */}
        {request.status_history?.length > 0 && (
          <div className="mt-5 pt-5 border-t border-white/10">
            <h4 className="text-xs font-semibold text-white/60 uppercase mb-2 flex items-center gap-1.5">
              <History className="h-3 w-3" />
              {language === 'ar' ? 'سجل التحديثات' : 'History'}
            </h4>
            <div className="space-y-1.5 max-h-52 overflow-y-auto pe-2">
              {[...request.status_history].reverse().map((h, i) => {
                const sInfo = statuses.find((s) => s.id === h.status) || { color: '#94a3b8', nameAr: h.status, nameEn: h.status, icon: Send };
                const SIco = sInfo.icon;
                return (
                  <div key={i} className="flex items-center gap-2 bg-black/30 border border-white/5 rounded-md px-2 py-1.5 text-xs" data-testid={`sr-history-${i}`}>
                    <span className="inline-flex items-center gap-1 font-semibold px-2 py-0.5 rounded" style={{ backgroundColor: `${sInfo.color}25`, color: sInfo.color }}>
                      <SIco className="h-3 w-3" />
                      {language === 'ar' ? sInfo.nameAr : sInfo.nameEn}
                    </span>
                    {h.price && (
                      <span className="text-[#d4af37] font-mono font-semibold">
                        {h.price} {h.currency}
                      </span>
                    )}
                    <span className="text-white/40">{new Date(h.at).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-AE', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                    {h.note && <span className="text-white/70 italic truncate">— {h.note}</span>}
                    <span className="text-white/40 ms-auto text-[10px]">{h.by}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Attachment lightbox — data: URLs can't open in a new tab, so preview in-app */}
      {previewDoc && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={() => setPreviewDoc(null)}
          data-testid="sr-doc-lightbox"
        >
          <button
            onClick={() => setPreviewDoc(null)}
            className="absolute top-4 end-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            data-testid="sr-doc-lightbox-close"
          >
            <X className="h-5 w-5" />
          </button>
          <div
            className="max-w-4xl w-full flex flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            {previewDoc.resolved.isImage ? (
              <img
                src={previewDoc.resolved.src}
                alt={previewDoc.doc.doc_name_ar || 'document'}
                className="max-h-[75vh] max-w-full object-contain rounded-xl border border-white/10 bg-black"
              />
            ) : previewDoc.resolved.mime === 'application/pdf' ? (
              <iframe
                src={previewDoc.resolved.src}
                title="document"
                className="w-full h-[75vh] rounded-xl bg-white"
              />
            ) : (
              <div className="text-white/70 text-sm py-10">
                {language === 'ar' ? 'لا يمكن معاينة هذا النوع — استخدم زر التحميل' : 'Preview unavailable — use download'}
              </div>
            )}
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <span className="text-sm text-white/80 font-semibold">
                {language === 'ar'
                  ? (previewDoc.doc.doc_name_ar || previewDoc.doc.doc_name_en)
                  : (previewDoc.doc.doc_name_en || previewDoc.doc.doc_name_ar)}
              </span>
              <Button
                size="sm"
                onClick={() => downloadResolvedDoc(previewDoc.resolved, previewDoc.doc.doc_name_ar || 'document')}
                className="bg-[#d4af37] hover:bg-[#c9a227] text-black"
                data-testid="sr-doc-lightbox-download"
              >
                <Download className="h-4 w-4 me-1" />
                {language === 'ar' ? 'تحميل' : 'Download'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────────

function InfoBox({ icon: Icon, label, value, dir }) {
  return (
    <div className="bg-black/30 rounded-xl p-4 border border-white/5">
      <div className="flex items-center gap-2 text-[#808080] mb-1">
        <Icon className="h-4 w-4" />
        <span className="text-xs">{label}</span>
      </div>
      <p className="text-white font-medium" dir={dir}>{value}</p>
    </div>
  );
}

function DocumentCard({ doc, index, language, onPreview }) {
  const isValid = doc.ai_validation?.clear === true;
  const resolved = resolveFileSrc(doc.file_url);
  const isImage = resolved.isImage;
  return (
    <div className="bg-black/40 border border-white/10 rounded-xl p-3 hover:border-white/25 transition-colors group" data-testid={`sr-doc-${index}`}>
      <div className="flex items-start gap-2 mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 overflow-hidden ${
          isImage ? 'bg-purple-500/15 text-purple-300' : 'bg-blue-500/15 text-blue-300'
        }`}>
          {isImage && resolved.src ? (
            <img src={resolved.src} alt="" className="w-full h-full object-cover" />
          ) : isImage ? <ImageIcon className="h-4 w-4" /> : <FileIcon className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {language === 'ar' ? (doc.doc_name_ar || doc.doc_name_en) : (doc.doc_name_en || doc.doc_name_ar)}
          </div>
          {doc.uploaded_at && (
            <div className="text-[10px] text-white/40 mt-0.5">
              {new Date(doc.uploaded_at).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-AE', {
                month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
              })}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 mb-2">
        {isValid ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
            <ShieldCheck className="h-3 w-3" />
            {language === 'ar' ? 'تم التحقق' : 'AI Verified'}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/30">
            <ShieldAlert className="h-3 w-3" />
            {language === 'ar' ? 'يحتاج مراجعة' : 'Needs Review'}
          </span>
        )}
      </div>

      {resolved.kind === 'truncated' && (
        <div className="flex items-start gap-1.5 text-[10.5px] text-amber-300/90 bg-amber-500/10 border border-amber-500/25 rounded-lg px-2 py-1.5" data-testid={`sr-doc-truncated-${index}`}>
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          {language === 'ar'
            ? 'المرفق وصل غير مكتمل من منصة العملاء (ملف مقصوص) — لا يمكن فتحه. يجب على منصة العملاء إرسال الملف كاملاً.'
            : 'Attachment arrived incomplete (truncated by the customer platform) — cannot be opened.'}
        </div>
      )}

      {(resolved.kind === 'data' || resolved.kind === 'url') && (
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onPreview(resolved)}
            className="inline-flex items-center gap-1 text-xs text-[#d4af37] hover:bg-[#d4af37]/10 px-2 py-1 rounded-md border border-[#d4af37]/30 transition"
            data-testid={`sr-doc-open-${index}`}
          >
            <Eye className="h-3 w-3" />
            {language === 'ar' ? 'عرض' : 'View'}
          </button>
          <button
            onClick={() => downloadResolvedDoc(resolved, doc.doc_name_ar || `document-${index + 1}`)}
            className="inline-flex items-center gap-1 text-xs text-white/70 hover:bg-white/10 px-2 py-1 rounded-md border border-white/15 transition"
            data-testid={`sr-doc-download-${index}`}
          >
            <Download className="h-3 w-3" />
            {language === 'ar' ? 'تحميل' : 'Download'}
          </button>
        </div>
      )}
    </div>
  );
}

function ChatBubble({ msg, index, language }) {
  const isBot = msg.role === 'bot' || msg.role === 'assistant';
  return (
    <div className={`flex gap-2 ${isBot ? '' : 'flex-row-reverse'}`} data-testid={`sr-msg-${index}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
        isBot ? 'bg-[#d4af37]/20 text-[#d4af37]' : 'bg-emerald-500/20 text-emerald-300'
      }`}>
        {isBot ? <Bot className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
      </div>
      <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${
        isBot
          ? 'bg-white/5 border border-white/10 text-white/90 rounded-tl-sm'
          : 'bg-emerald-500/15 border border-emerald-500/30 text-white rounded-tr-sm'
      }`}>
        <div className="text-sm whitespace-pre-wrap break-words">{msg.message}</div>
        {msg.timestamp && (
          <div className="text-[9px] text-white/40 mt-1 font-mono">
            {new Date(msg.timestamp).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-AE', {
              month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
            })}
          </div>
        )}
      </div>
    </div>
  );
}
